import { EntityScreenRow } from 'app/models/entity-screen-row'
import { createReducer } from 'app/state/utils'
import {
  acceptedIds,
  invalidate,
  oneId,
  outdated,
  withRows,
} from 'app/state/utils/generation'
import * as entityTypes from 'app/state/ducks/entities/types'
import * as entityTypeTypes from 'app/state/ducks/entity-type/types'
import * as entityProfileTypes from 'app/state/ducks/entity-profile/types'
import * as types from './types'

export interface EntityScreenState {
  rows: EntityScreenRow[]
  isLoading: boolean
  /**
   * True once a write has happened that the rows cannot be patched for, or a
   * read came back from before a write. The thunk reads once more for the
   * second; the grid reloads once, while on screen, for the first.
   */
  stale: boolean
  /**
   * Bumped by every write the store learns of. A request records the
   * generation it started under; a response from an older generation is
   * thrown away, because it predates a write the rows already reflect
   * (utils/generation.ts).
   */
  generation: number
  /**
   * Entity type and profile id → name, from those lists as they pass through
   * the store (the entity form loads both when it opens). A saved entity's
   * type and profiles are named from them, so a type or profile on no other
   * row is still shown without a reload. Not a write: never moves the generation.
   */
  typeNames: Record<string, string>
  profileNames: Record<string, string>
}

const initialState: EntityScreenState = {
  rows: [],
  isLoading: false,
  stale: false,
  generation: 0,
  typeNames: {},
  profileNames: {},
}

type Row = EntityScreenRow

/** Every row of `rows` whose id is in `ids` flipped to `status`. */
const withStatus = (state: EntityScreenState, ids: string[], status: Row['status']) => {
  const flipped = new Set(ids.filter(Boolean))
  if (flipped.size === 0) return state
  // Always a NEW array, even when no row matched: the row may be in a read
  // still in flight, and withRows must move the generation so that read is
  // dropped and taken again (utils/generation.ts).
  return withRows(
    state,
    state.rows.map(row => (flipped.has(row.id) ? { ...row, status } : row)),
  )
}

/**
 * The type and profile names the statement resolved server-side, read back
 * off the rows, with the lists' own names on top (fresher: the form that saved
 * the entity loaded them when it opened).
 */
const namesOf = (state: EntityScreenState) => {
  const typeNames = new Map<string, string>()
  const profileNames = new Map<string, string>()
  for (const row of state.rows) {
    if (row.entityTypeId && row.entityTypeName) typeNames.set(row.entityTypeId, row.entityTypeName)
    row.entityProfileIds.forEach((id, i) => {
      if (row.entityProfileNames[i]) profileNames.set(id, row.entityProfileNames[i])
    })
  }
  for (const [id, name] of Object.entries(state.typeNames)) typeNames.set(id, name)
  for (const [id, name] of Object.entries(state.profileNames)) profileNames.set(id, name)
  return { typeNames, profileNames }
}

/** A key as the ledger answers it (`{ admin, id }`) or as a form may hold it (the id). */
const keyId = (key: unknown): string | null =>
  typeof key === 'string' ? key || null : ((key as { id?: string } | null)?.id ?? null)

/**
 * The grid's columns of a saved entity. The create and edit endpoints answer
 * with the whole record as the ledger holds it, so every field is taken from
 * it as it is: an optional field the user cleared is cleared here too.
 */
const fieldsOf = (saved: any, state: EntityScreenState) => {
  const { typeNames, profileNames } = namesOf(state)
  const entityTypeId = keyId(saved.entityType)
  const entityProfileIds = (Array.isArray(saved.entityProfile) ? saved.entityProfile : [])
    .map(keyId)
    .filter((id: string | null): id is string => !!id)
  return {
    entityName: saved.entityName ?? '',
    internalId: saved.internalId ?? '',
    systemId: saved.systemId ?? '',
    mei: saved.mei ?? '',
    kycType: saved.kycType ?? '',
    entityTypeId,
    entityTypeName: (entityTypeId && typeNames.get(entityTypeId)) || '',
    entityProfileIds,
    entityProfileNames: entityProfileIds.map((id: string) => profileNames.get(id) ?? ''),
    parent: saved.parent ?? '',
    fundManager: saved.fundManager ?? '',
    taxFormStatus: saved.taxFormStatus ?? '',
    kycStatus: saved.kycStatus ?? '',
  }
}

/** The saved record a create / edit action carries (the Axios response's data), if it carried one. */
const savedRecord = (action: { payload?: any }) => {
  const saved = action.payload?.data
  return saved?.id && typeof saved?.entityName === 'string' ? saved : undefined
}

/** The thunks tag a saved record's status in lower or title case. */
const statusOf = (status: unknown): Row['status'] =>
  String(status ?? '').toLowerCase() === 'active' ? 'Active' : 'Inactive'

/**
 * The type or profile list (GET_ENTITY_TYPES / GET_ENTITY_PROFILES) — loaded
 * by the entity form when it opens, and by any page that needs it. Its names
 * are kept for the next saved entity, and fill any name a row is still missing;
 * never a status or a value, and not a write, so the generation does not move.
 */
const ingestNames = (key: 'typeNames' | 'profileNames') => (state: EntityScreenState, action: any) => {
  const listed = (Array.isArray(action.payload) ? action.payload : []).filter(
    (item: any) => item?.id && item?.name,
  )
  if (listed.length === 0) return state
  const names = { ...state[key] }
  for (const item of listed) names[item.id] = item.name
  let changed = false
  const rows = state.rows.map(row => {
    if (key === 'typeNames') {
      if (row.entityTypeName || !row.entityTypeId || !names[row.entityTypeId]) return row
      changed = true
      return { ...row, entityTypeName: names[row.entityTypeId] }
    }
    const entityProfileNames = row.entityProfileIds.map(
      (id, i) => row.entityProfileNames[i] || names[id] || '',
    )
    if (entityProfileNames.every((name, i) => name === row.entityProfileNames[i])) return row
    changed = true
    return { ...row, entityProfileNames }
  })
  return { ...state, [key]: names, rows: changed ? rows : state.rows }
}

const entityScreenReducer = createReducer(initialState)({
  [types.GET_ENTITY_SCREEN]: (
    state: EntityScreenState,
    action: { payload: { rows: Row[]; generation?: number } },
  ) => {
    // Fetched before a write the store has since applied: these rows would
    // undo it. Drop them and leave the store stale; the thunk reads once more.
    if (outdated(state, action.payload.generation)) return { ...state, stale: true }
    return { ...state, rows: action.payload.rows, stale: false }
  },
  [types.START_LOADING_ENTITY_SCREEN]: (state: EntityScreenState) => ({
    ...state,
    isLoading: true,
  }),
  [types.ENTITY_SCREEN_LOAD_FAILED]: (state: EntityScreenState) => ({
    ...state,
    stale: false,
  }),
  [types.STOP_LOADING_ENTITY_SCREEN]: (state: EntityScreenState) => ({
    ...state,
    isLoading: false,
  }),
  [types.INVALIDATE_ENTITY_SCREEN]: invalidate,

  // The grid reads this store, but every entity command is dispatched through
  // the entities duck — by the row menu, by the details page, by the Create
  // Entity form. Rather than have every caller remember to refresh the grid,
  // the grid's own store listens for the same successes and patches its rows —
  // never refetches, because PQS projects asynchronously and a read straight
  // after the command can still return the old contracts.

  // Activation assigns the system id (on the first one), and the activated
  // record in the answer carries it.
  [entityTypes.ACTIVE_ENTITY]: (state: EntityScreenState, action: any) => {
    const [id] = oneId(action)
    if (!id) return state
    return withRows(
      state,
      state.rows.map(row =>
        row.id === id
          ? { ...row, status: 'Active', systemId: action.payload.systemId || row.systemId }
          : row,
      ),
    )
  },
  [entityTypes.INACTIVE_ENTITY]: (state: EntityScreenState, action: any) =>
    withStatus(state, oneId(action), 'Inactive'),
  [types.SET_ENTITY_SCREEN_STATUSES]: (
    state: EntityScreenState,
    action: { payload: { ids: string[]; status: Row['status'] } },
  ) => withStatus(state, action.payload.ids, action.payload.status),
  // The delete answers per id; a row the ledger refused to delete stays.
  [entityTypes.DELETE_ENTITIES]: (state: EntityScreenState, action: any) => {
    const gone = new Set(acceptedIds(action.payload))
    return gone.size
      ? withRows(state, state.rows.filter(row => !gone.has(row.id)))
      : state
  },
  [entityTypes.ADD_ENTITIES]: (state: EntityScreenState, action: any) => {
    const saved = savedRecord(action)
    if (!saved) return invalidate(state)
    const row: Row = { id: saved.id, status: statusOf(saved.status), ...fieldsOf(saved, state) }
    return withRows(state, [row, ...state.rows.filter(r => r.id !== saved.id)])
  },
  // An edit re-creates the entity as inactive (EditActive / EditInactive both
  // answer an InactiveEntity); the thunk tags the record so.
  [entityTypes.EDIT_ENTITIES]: (state: EntityScreenState, action: any) => {
    const saved = savedRecord(action)
    if (!saved) return invalidate(state)
    return withRows(
      state,
      state.rows.map(row =>
        row.id === saved.id
          ? { ...row, ...fieldsOf(saved, state), status: statusOf(saved.status) }
          : row,
      ),
    )
  },
  [entityTypeTypes.GET_ENTITY_TYPES]: ingestNames('typeNames'),
  [entityProfileTypes.GET_ENTITY_PROFILES]: ingestNames('profileNames'),
})

export default entityScreenReducer
